import React from 'react'
import BleedFrame from '@/components/app/document/overlays/bleed-frame'
import LiveArea from '@/components/app/document/overlays/live-area'
import {
  nonSpineMarginEdgesForPage,
  spineMarginEdgeForPage,
} from '@/components/app/document/overlays/margins-overlay'
import PrinterMarks from '@/components/app/document/overlays/printer-marks'
import ColumnsRowsOverlay from '@/components/app/document/overlays/columns-rows-overlay'
import { PRINT_BLEED_MARGIN_PT } from '@/lib/document/print-bleed'
import { computeSnappedPageTrimWidth } from '../helpers/page-trim-width'
import { InnerTrimBleedFrame, SpineGutterOverlay } from '../print-bleed'
import { buildDoubleSpreadPageConfigs } from './helpers/double-spread-page-configs'

type DoubleSpreadDocumentProps = {
  bleedWidth?: number
  bleedHeight?: number
  trimWidth?: number
  trimHeight?: number
  bleedMargin?: number
  liveInset?: number
  marginInside?: number
  marginOutside?: number
  marginTop?: number
  marginBottom?: number
  showPrinterMarks?: boolean
  columns?: number
  rows?: number
  colGutter?: number
  rowGutter?: number
  showLeftPageGuides?: boolean
  showRightPageGuides?: boolean
  scale?: number
  /** Bindery gutter (center) when print bleed is on — below layout grid (z-10) and dashed trim (z-9). */
  centerGap?: number
  showSpineGutter?: boolean
  /**
   * When true, draw the in-trim yellow frame in this tree (z≈7). The editor’s
   * `DoubleSpreadBleedOverlayAboveShapes` + `PrintBleedTints` (z6) also paints the full L-ring; set
   * this false when that overlay is used so the two do not stack (inner band would read darker).
   */
  showPrintBleed?: boolean
  /** In-trim L-ring here; default true so non-editor stories keep the frame when `showPrintBleed` is on. */
  showInnerTrimBleedFrame?: boolean
  /**
   * Tiled layout grid (full/half): above bleed tints. The solid spine bar itself is not drawn in
   * this tree — see `DoubleSpreadSpineBarAboveShapes`, rendered by the editor above shapes and the
   * dashed trim, same as the margin lines.
   */
  tiledGridOverlay?: React.ReactNode
  /**
   * Spread/page backgrounds, painted right above the paper and below everything else in the frame
   * (bleed tints, grids, guides) — and below all artwork, which renders outside this tree.
   * Positioned in canvas pts, the same space `.doc-page-fill` uses here.
   */
  pageBackground?: React.ReactNode
  /**
   * Bindery seam X (pt), shared with the ruler "0" tick, spine bar, dashed trim, gutter tint, and
   * margin guides so the page-trim split can never drift a fraction of a point from any of them.
   * When set (the editor always passes it), both page halves split at this X instead of each
   * independently flooring `trimWidth / 2`. Omit in stories for pure layout math.
   */
  spreadOriginSnapCanvasX?: number
}

const DoubleSpreadDocument: React.FC<DoubleSpreadDocumentProps> = ({
  bleedWidth = 1248,
  bleedHeight = 816,
  trimWidth = 1224,
  trimHeight = 792,
  bleedMargin = PRINT_BLEED_MARGIN_PT,
  liveInset = 24,
  marginInside = 24,
  marginOutside = 24,
  marginTop = 24,
  marginBottom = 24,
  showPrinterMarks = false,
  columns = 0,
  rows = 0,
  colGutter = 24,
  rowGutter = 24,
  showLeftPageGuides = true,
  showRightPageGuides = true,
  scale = 1,
  centerGap = 0,
  showSpineGutter = false,
  showPrintBleed = false,
  showInnerTrimBleedFrame = true,
  tiledGridOverlay = null,
  pageBackground = null,
  spreadOriginSnapCanvasX,
}) => {
  const leftTrimLeft = bleedMargin
  /**
   * When `spreadOriginSnapCanvasX` is given (the editor always passes it), both page halves split
   * at that device-pixel-snapped seam — the same one the ruler "0" tick, spine bar, and spine grid
   * column use — rather than each independently flooring `trimWidth / 2`, which can round to a
   * fraction of a point off it and make the spine bar sit visibly off-center between the two pages.
   * Falls back to the raw floor for callers that don't pass it (e.g. stories/tests).
   */
  const pageTrimWidth = computeSnappedPageTrimWidth(
    leftTrimLeft,
    trimWidth,
    spreadOriginSnapCanvasX
  )
  const rightTrimLeft = leftTrimLeft + pageTrimWidth
  const topInset = Number.isFinite(marginTop) ? marginTop : liveInset
  const bottomInset = Number.isFinite(marginBottom) ? marginBottom : liveInset
  const outsideInset = Number.isFinite(marginOutside) ? marginOutside : liveInset
  const insideInset = Number.isFinite(marginInside) ? marginInside : liveInset
  const liveHeight = trimHeight - topInset - bottomInset

  const pageConfigs = buildDoubleSpreadPageConfigs({
    leftTrimLeft,
    rightTrimLeft,
    pageTrimWidth,
    bleedMargin,
    topInset,
    outsideInset,
    insideInset,
    liveHeight,
    showLeftPageGuides,
    showRightPageGuides,
  })

  const renderHorizontalSafeBand = (
    key: string,
    left: number,
    top: number,
    height: number,
    label: string | null
  ) => (
    <div
      key={key}
      className="doc-safe-area-band"
      style={{ left, top, width: pageTrimWidth, height, zIndex: 6 }}
    >
      {label != null && label !== '' ? (
        <span
          className="doc-band__label"
          style={{
            position: 'absolute',
            top: 2,
            left: '50%',
            transform: 'translateX(-50%)',
          }}
        >
          {label}
        </span>
      ) : null}
    </div>
  )

  /** Gray margin strip only; inner bleed copy lives in `PrintBleedTintOverlay` (perimeter yellow lanes). */
  const renderVerticalSafeBand = (key: string, left: number) => (
    <div
      key={key}
      className="doc-safe-area-band"
      style={{
        left,
        top: bleedMargin + topInset,
        width: outsideInset,
        height: liveHeight,
        zIndex: 6,
      }}
    />
  )

  return (
    <BleedFrame
      width={bleedWidth}
      height={bleedHeight}
      paintBackground={showPrintBleed}
      className="z-[1]"
    >
      {showPrinterMarks && (
        <PrinterMarks trimLeft={bleedMargin} trimTop={bleedMargin} trimWidth={trimWidth} />
      )}

      {pageConfigs.map((page) => (
        <div
          key={`page-${page.key}`}
          className="doc-page-fill"
          style={{
            left: page.trimLeft,
            top: bleedMargin,
            width: pageTrimWidth,
            height: trimHeight,
          }}
        />
      ))}

      {pageBackground}

      {showPrintBleed &&
        showInnerTrimBleedFrame &&
        pageConfigs.map((page) => (
          <InnerTrimBleedFrame
            key={`inner-trim-spine-${page.key}`}
            trimLeft={page.trimLeft}
            trimTop={bleedMargin}
            trimWidth={pageTrimWidth}
            trimHeight={trimHeight}
            visible
            zIndexBase={7}
            edges={
              centerGap > 0
                ? nonSpineMarginEdgesForPage(page.marginInsideOnLeft)
                : [spineMarginEdgeForPage(page.marginInsideOnLeft)]
            }
          />
        ))}

      <SpineGutterOverlay
        bleedHeight={bleedHeight}
        bleedMargin={bleedMargin}
        trimHeight={trimHeight}
        trimWidth={trimWidth}
        centerGap={centerGap}
        visible={showSpineGutter}
      />

      {tiledGridOverlay}

      {pageConfigs.flatMap((page) => [
        renderHorizontalSafeBand(`${page.key}-top`, page.trimLeft, bleedMargin, topInset, null),
        renderHorizontalSafeBand(
          `${page.key}-bottom`,
          page.trimLeft,
          bleedMargin + trimHeight - bottomInset,
          bottomInset,
          null
        ),
      ])}

      {pageConfigs.map((page) =>
        renderVerticalSafeBand(`${page.key}-vertical`, page.verticalBandLeft)
      )}

      {insideInset > 0 &&
        pageConfigs.map((page) => (
          <div
            key={`${page.key}-inside-band`}
            className="doc-safe-area-band"
            style={{
              left: page.insideBandLeft,
              top: bleedMargin + topInset,
              width: insideInset,
              height: liveHeight,
              zIndex: 5,
            }}
          />
        ))}

      {pageConfigs.map((page) => (
        <LiveArea
          key={`live-${page.key}`}
          left={page.liveLeft}
          top={bleedMargin + topInset}
          width={page.liveWidth}
          height={liveHeight}
        />
      ))}

      {pageConfigs.map((page) => (
        <ColumnsRowsOverlay
          key={`grid-${page.key}`}
          trimLeft={page.gridLeft}
          trimTop={page.gridTop}
          trimWidth={page.gridWidth}
          trimHeight={page.gridHeight}
          columns={page.guidesEnabled ? columns : 0}
          rows={page.guidesEnabled ? rows : 0}
          colGutter={colGutter}
          rowGutter={rowGutter}
          scale={scale}
        />
      ))}
    </BleedFrame>
  )
}

export default DoubleSpreadDocument
